'use client';

import { Logo } from '@/components/icons';
import { SidebarHistoryPanel } from '@/components/sidebar-history/sidebar-history-panel';
import { SmartLink } from '@/components/smart-link';
import { UserProfileMenuContent } from '@/components/top-menu-user-dropdown';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from '@/components/ui/sidebar';
import { getUserAvatarSrc } from '@/lib/avatar';
import { useAuth } from '@/providers/auth-provider';
import {
  BrainCircuitIcon,
  HistoryIcon,
  LanguagesIcon,
  Loader2Icon,
  NewspaperIcon,
  SearchIcon,
} from 'lucide-react';
import Image from 'next/image';
import { usePathname, useSearchParams } from 'next/navigation';
import { type MouseEvent, useEffect, useRef, useState } from 'react';

const SIDEBAR_HISTORY_TYPE_STORAGE_KEY = 'suppr.sidebar.history-type';

function isMainLinkActive(pathname: string, href: string) {
  if (href === '/') {
    return pathname === '/';
  }

  return pathname === href || pathname.startsWith(`${href}/`);
}

function isPlainLeftClick(event: MouseEvent<HTMLAnchorElement>) {
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  );
}

function getPreferredHistoriesHref() {
  if (typeof window === 'undefined') {
    return '/histories';
  }

  const savedType = window.localStorage.getItem(
    SIDEBAR_HISTORY_TYPE_STORAGE_KEY,
  );

  return savedType === 'translate' || savedType === 'deep-research'
    ? `/histories?type=${savedType}`
    : '/histories?type=search';
}

export function SideMenu() {
  const { user } = useAuth();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { state } = useSidebar();
  const isCollapsed = state === 'collapsed';
  const [showExpandedHistory, setShowExpandedHistory] = useState(!isCollapsed);
  const [pendingHref, setPendingHref] = useState<string | null>(null);
  const pendingTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (pendingTimerRef.current !== null) {
        window.clearTimeout(pendingTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isCollapsed) {
      const timer = window.setTimeout(() => {
        setShowExpandedHistory(false);
      }, 0);
      return () => window.clearTimeout(timer);
    }

    const timer = window.setTimeout(() => {
      setShowExpandedHistory(true);
    }, 200);

    return () => window.clearTimeout(timer);
  }, [isCollapsed]);

  const currentSessionId = searchParams.get('sid');
  const currentTranslationId = searchParams.get('id');
  const currentDeepResearchId = pathname.startsWith('/deep-research/')
    ? pathname.split('/')[2]
    : null;
  const historiesHref = isCollapsed ? getPreferredHistoriesHref() : '/histories';

  const mainLinks: Array<{
    href: string;
    icon: typeof SearchIcon;
    label: string;
    protected?: boolean;
  }> = [
    {
      label: '文献检索',
      href: '/',
      icon: SearchIcon,
    },
    {
      label: '文档翻译',
      href: user ? '/translate/upload' : '/translate',
      icon: LanguagesIcon,
    },
    {
      label: '深度研究',
      href: '/deep-research',
      icon: BrainCircuitIcon,
    },
    {
      label: '学术资讯',
      href: '/news',
      icon: NewspaperIcon,
    },
  ];

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="px-3">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <SmartLink href="/" mode="always">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center">
                  <Logo className="w-6! h-6!" />
                </div>
                {!isCollapsed && (
                  <div className="flex flex-col gap-0.5 leading-none">
                    <span className="font-bold text-foreground">Suppr</span>
                    <span className="text-xs font-semibold">超能文献</span>
                  </div>
                )}
              </SmartLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent className="min-h-0 overflow-hidden">
        <div className="flex h-full min-h-0 flex-col">
          <SidebarGroup className="shrink-0 px-3">
            <SidebarGroupContent>
              <SidebarMenu>
                {mainLinks.map((link) => {
                  const isActive = isMainLinkActive(pathname, link.href);
                  const isPending = pendingHref === link.href && !isActive;
                  const Icon = isPending ? Loader2Icon : link.icon;

                  return (
                    <SidebarMenuItem key={link.href}>
                      <SidebarMenuButton
                        asChild
                        isActive={isActive}
                        tooltip={
                          isPending ? `${link.label}打开中...` : link.label
                        }
                      >
                        <SmartLink
                          href={link.href}
                          authenticated={!!user}
                          mode={link.protected ? 'guarded' : 'always'}
                          aria-busy={isPending}
                          onClick={(event) => {
                            if (!isActive && isPlainLeftClick(event)) {
                              if (pendingTimerRef.current !== null) {
                                window.clearTimeout(pendingTimerRef.current);
                              }
                              setPendingHref(link.href);
                              pendingTimerRef.current = window.setTimeout(
                                () => {
                                  setPendingHref(null);
                                  pendingTimerRef.current = null;
                                },
                                10000,
                              );
                            }
                          }}
                        >
                          <Icon
                            className={isPending ? 'animate-spin' : undefined}
                          />
                          <span className="font-semibold text-foreground">
                            {link.label}
                          </span>
                        </SmartLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <SidebarGroup className="shrink-0 px-3">
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={pathname.startsWith('/landing/zotero')}
                      tooltip="Zotero 插件"
                    >
                      <SmartLink href="/landing/zotero" mode="always">
                        <Image
                          src="/img/zotero-logo.png"
                          width={16}
                          height={16}
                          alt="Suppr Zotero 插件"
                          sizes="16px"
                        />
                        <span className="font-semibold text-foreground">
                          Zotero 插件
                        </span>
                      </SmartLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
                {user && isCollapsed ? (
                  <SidebarMenu className="mt-2">
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        asChild
                        isActive={pathname.startsWith('/histories')}
                        tooltip="历史记录"
                      >
                        <SmartLink
                          href={historiesHref}
                          authenticated={!!user}
                          mode="intent"
                        >
                          <HistoryIcon />
                          <span className="font-semibold text-foreground">
                            历史记录
                          </span>
                        </SmartLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </SidebarMenu>
                ) : null}
              </SidebarGroupContent>
            </SidebarGroup>
            {user && showExpandedHistory ? (
              <div className="min-h-0 flex-1 overflow-hidden">
                <SidebarHistoryPanel
                  pathname={pathname}
                  currentSessionId={currentSessionId}
                  currentTranslationId={currentTranslationId}
                  currentDeepResearchId={currentDeepResearchId}
                  isAuthenticated={!!user}
                />
              </div>
            ) : null}
          </div>
        </div>
      </SidebarContent>
      {user ? (
        <SidebarFooter className="px-3 pt-0 pb-3">
          <SidebarMenu>
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton size="lg" tooltip="账户">
                    <Avatar
                      className={
                        isCollapsed ? 'h-8 w-8 shrink-0' : 'h-9 w-9 shrink-0'
                      }
                    >
                      <AvatarImage
                        src={getUserAvatarSrc(user)}
                        alt={user.nickname}
                      />
                      <AvatarFallback>
                        {user.nickname?.[0] || '?'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                      <span className="truncate font-semibold text-foreground">
                        {user.nickname}
                      </span>
                      <div className="pt-0.5">
                        <span
                          className={
                            user.membership
                              ? 'inline-flex max-w-full items-center rounded-full border border-white/10 bg-linear-to-r from-gray-700 via-neutral-700 to-stone-700 px-2 py-0.75 text-[9px] font-semibold text-white shadow-sm dark:border-white/10 dark:from-gray-700 dark:via-neutral-700 dark:to-stone-700 dark:text-white'
                              : 'inline-flex max-w-full items-center rounded-full bg-muted px-2 py-0.75 text-[9px] font-semibold text-muted-foreground'
                          }
                        >
                          <span className="truncate">
                            {user.pointInfo?.tier_name}
                          </span>
                        </span>
                      </div>
                    </div>
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="center"
                  side="top"
                  className="w-52 rounded-2xl border bg-popover shadow-none"
                >
                  <UserProfileMenuContent user={user} />
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      ) : null}
      <SidebarRail />
    </Sidebar>
  );
}
